import api from '@/services/api';
import { apiErrorMessage } from '@/services/captureApi';
import type { QualityTier } from '@/types/models';

export interface Kubicle1aLesson {
  title: string;
  script: string;
  qualityTier: QualityTier;
}

interface AppliedLocks {
  voiceId?: string | null;
  backgroundMusic?: boolean | null;
  script?: unknown;
  templateLibrary?: { name: string; version: string };
}

const brief = (title: string) => `Make this lesson: ${title}. Use the script as given.`;

function lockTags(applied: AppliedLocks): string {
  const tags: string[] = [];
  if (applied.voiceId) tags.push(`[voice: ${applied.voiceId}]`);
  if (typeof applied.backgroundMusic === 'boolean') tags.push(`[background-music: ${applied.backgroundMusic ? 'on' : 'off'}]`);
  if (applied.script) tags.push('[script: locked]');
  if (applied.templateLibrary) tags.push(`[template-library: ${applied.templateLibrary.name}@${applied.templateLibrary.version}]`);
  return tags.join(' ');
}

/**
 * Creates a Kubicle 1A lesson the way the API does (POST /api/v2/videos with `pipeline: kubicle-1a`), so the project
 * carries the workflow's locks and the template library version it pins. The first turn is not sent here: the API's
 * kickoff needs a PAT, so the web chat sends `kickoff` over the user's own chat session. It opens with the 1A command
 * (the chat routes a story-development turn to audio-first unless a command leads), then the applied locks as tags,
 * the brief and the script, as the API's kickoff carries them.
 */
export async function startKubicle1a({ title, script, qualityTier }: Kubicle1aLesson): Promise<{ videoId: string; kickoff: string }> {
  const name = title.trim();
  if (!name) throw new Error('A lesson title is needed');
  if (!script.trim()) throw new Error('The lesson script is needed');
  const form = new FormData();
  form.append('pipeline', 'kubicle-1a');
  form.append('title', name);
  form.append('instructions', brief(name));
  form.append('script', script);
  form.append('voiceId', 'Kore');
  form.append('qualityTier', qualityTier);
  let data: { videoId: string; applied?: AppliedLocks };
  try {
    ({ data } = await api.post('/v2/videos', form, { headers: { 'Idempotency-Key': crypto.randomUUID() } }));
  } catch (err) {
    throw new Error(apiErrorMessage(err));
  }
  const command = ['/create-video-kubicle-1a', lockTags(data.applied ?? {})].filter(Boolean).join(' ');
  const kickoff = [command, brief(name), `Script:\n${script.trim()}`].join('\n\n');
  return { videoId: data.videoId, kickoff };
}
